import { Card, CardContent, Box, Typography, Avatar, Chip, Stack } from '@mui/material'
import { EmojiEvents, LocalFireDepartment } from '@mui/icons-material'
import { motion } from 'framer-motion'
import type { FamilyMember } from '@/types'
import { calculateLevel } from '@/lib/achievements'

interface LeaderboardCardProps {
  members: FamilyMember[]
  currentUserId: string | null
}

export default function LeaderboardCard({ members, currentUserId }: LeaderboardCardProps) {
  const sortedMembers = [...members].sort((a, b) => b.points - a.points)

  const getMedalEmoji = (index: number) => {
    switch (index) {
      case 0:
        return '🥇'
      case 1:
        return '🥈'
      case 2:
        return '🥉'
      default:
        return null
    }
  }

  const getRankColor = (index: number) => {
    switch (index) {
      case 0:
        return 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
      case 1:
        return 'linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%)'
      case 2:
        return 'linear-gradient(135deg, #cd7f32 0%, #b87333 100%)'
      default:
        return 'transparent'
    }
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <EmojiEvents sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Family Leaderboard
          </Typography>
        </Box>

        <Stack spacing={2}>
          {sortedMembers.map((member, index) => {
            const { level } = calculateLevel(member.points)
            const isCurrentUser = member.id === currentUserId
            const medal = getMedalEmoji(index)

            return (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 3,
                    background: isCurrentUser
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                      : index < 3
                      ? `${getRankColor(index)}20`
                      : 'transparent',
                    border: isCurrentUser ? '2px solid' : '1px solid',
                    borderColor: isCurrentUser ? 'primary.main' : 'divider',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  {/* Rank */}
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: getRankColor(index),
                      color: index < 3 ? 'white' : 'text.secondary',
                      fontWeight: 700,
                      fontSize: '1.125rem',
                    }}
                  >
                    {medal || `#${index + 1}`}
                  </Box>

                  {/* Avatar */}
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: member.color,
                      border: '3px solid',
                      borderColor: index < 3 ? 'white' : 'transparent',
                      boxShadow: index < 3 ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                    }}
                    src={member.avatar_url || undefined}
                  >
                    {member.name[0]}
                  </Avatar>

                  {/* Name and Level */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {member.name}
                      {isCurrentUser && (
                        <Chip
                          label="You"
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Level {level}
                      </Typography>
                      {member.streak > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalFireDepartment
                            sx={{ fontSize: 14, color: 'error.main' }}
                          />
                          <Typography variant="caption" color="error.main" fontWeight={600}>
                            {member.streak} day streak
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Points */}
                  <Box
                    sx={{
                      textAlign: 'right',
                      bgcolor: index < 3 ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.05)',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: index < 3 ? 'text.primary' : 'inherit',
                      }}
                    >
                      {member.points}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      points
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            )
          })}
        </Stack>

        {sortedMembers.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No family members yet
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
